import React, { useMemo, useState, useEffect } from 'react';
import { calculateSeatPosition, getTableDimensions, calculateCardPosition } from '../../utils/seatPositioning';
import { getSeatUIState, getGlobalUIState } from '../../utils/phaseState';
import TableFelt from './TableFelt';
import PotDisplay from './PotDisplay';
import PlayerSeat from './PlayerSeat';
import PlayerHand from './PlayerHand';
import PlayersHudPanel from '../hud/PlayersHudPanel';
import PhaseControls from '../hud/PhaseControls';

/**
 * HudPanelContainer Component
 * Wrapper for the HUD panel with hide/show functionality
 * Responsive for both desktop and mobile
 */
function HudPanelContainer({ children }) {
  const [isVisible, setIsVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const panelWidth = isMobile ? '100%' : '280px';
  const buttonRight = isVisible ? (isMobile ? '100%' : '280px') : '0px';
  
  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        style={{
          position: 'fixed',
          right: buttonRight,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1000,
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px',
          padding: isMobile ? '16px 12px' : '12px 8px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          transition: 'right 0.3s ease',
          fontSize: isMobile ? '1rem' : '0.875rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: isMobile ? '50px' : '40px',
          touchAction: 'manipulation' // Better mobile touch handling
        }}
        aria-label={isVisible ? 'Hide players panel' : 'Show players panel'}
      >
        {isVisible ? '◄' : '►'}
      </button>
      
      {/* HUD Panel */}
      <div
        style={{
          width: panelWidth,
          maxWidth: isMobile ? '100vw' : '280px',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
          transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
          position: isMobile ? 'fixed' : 'relative',
          right: isMobile ? 0 : 'auto',
          top: isMobile ? 0 : 'auto',
          height: isMobile ? '100vh' : 'auto',
          zIndex: 999,
          overflowY: isMobile ? 'auto' : 'visible',
          background: isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent'
        }}
      >
        <div style={{
          background: '#ffffff',
          borderRadius: isMobile ? '0' : '12px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          overflow: 'visible' // No scrolling - expand to fit content
        }}>
          {children}
        </div>
      </div>
    </>
  );
}

/**
 * TableCentric Component
 * Main table component with modern poker-style layout
 * Replaces placard-based layout with table-centric design
 */
export default function TableCentric({ table, player, socket, onQuit }) {
  const [viewportSize, setViewportSize] = useState({ 
    width: typeof window !== 'undefined' ? window.innerWidth : 1920, 
    height: typeof window !== 'undefined' ? window.innerHeight : 1080 
  });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false
  );
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setViewportSize({ width, height });
      setIsMobile(width < 768);
      setIsLandscape(width > height);
    };
    
    // Initial check
    handleResize();
    
    // Listen for resize and orientation changes
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Delay to allow orientation change to complete
      setTimeout(handleResize, 100);
    });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  const allSeats = useMemo(() => (table?.players || []).concat(table?.bots || []), [table]);
  const state = table?.state ?? {};
  const myIdx = allSeats.findIndex(p => p.playerID === player?.playerID);
  
  // Debug logging for state updates
  useEffect(() => {
    console.log(`DEBUG: TableCentric - State update:`, {
      phase: state.phase,
      hands: state.hands?.map((h, i) => `${i}:${h?.length || 0} cards`).join(', ') || 'NO HANDS',
      handsArray: state.hands,
      activePlayerIdx: state.activePlayerIdx,
      localPlayerIdx: myIdx,
      localPlayerID: player?.playerID,
      knocked: state.knocked,
      discarded: state.discarded,
      roundContributions: state.roundContributions,
      anteCollected: state.anteCollected,
      dealerIdx: state.dealerIdx
    });
  }, [state.phase, state.hands, state.activePlayerIdx, myIdx, player?.playerID, state.knocked, state.discarded, state.roundContributions, state.anteCollected, state.dealerIdx]);
  
  // Calculate table dimensions - scale down to fit all players with text visible
  // Account for: HUD (280px), PhaseControls (~150px), padding, seat overflow including player text below avatars
  const tableDims = useMemo(() => {
    // Mobile-specific adjustments
    let hudWidth = isMobile ? 0 : 280;
    let controlsHeight = isMobile ? (isLandscape ? 80 : 120) : 150; // Smaller controls on mobile
    let padding = isMobile ? (isLandscape ? 15 : 10) : 30;
    let seatOverflow = isMobile ? (isLandscape ? 180 : 200) : 220; // Reduced overflow on mobile
    
    // For mobile portrait, account for HUD overlay
    if (isMobile && !isLandscape) {
      // HUD takes full width overlay, so no width deduction
      hudWidth = 0;
    }
    
    // Available space accounting for all UI elements including player text
    const availableWidth = Math.max(viewportSize.width - hudWidth - padding * 2 - seatOverflow, 300);
    const availableHeight = Math.max(viewportSize.height - controlsHeight - padding * 2 - seatOverflow, 250);
    
    const dims = getTableDimensions(availableWidth, availableHeight);
    
    // Scale down if needed to ensure seats + player text don't go off-screen
    const maxTableWidth = viewportSize.width - hudWidth - padding * 2 - seatOverflow;
    const maxTableHeight = viewportSize.height - controlsHeight - padding * 2 - seatOverflow;
    
    let finalWidth = dims.width;
    let finalHeight = dims.height;
    
    // Scale down if table is too large
    if (finalWidth > maxTableWidth) {
      const scale = maxTableWidth / finalWidth;
      finalWidth = maxTableWidth;
      finalHeight = finalHeight * scale;
    }
    if (finalHeight > maxTableHeight) {
      const scale = maxTableHeight / finalHeight;
      finalHeight = maxTableHeight;
      finalWidth = finalWidth * scale;
    }
    
    // Apply additional scaling to ensure everything fits
    const scaleFactor = 0.85; // Scale down by 15% to ensure player text fits
    finalWidth = finalWidth * scaleFactor;
    finalHeight = finalHeight * scaleFactor;
    
    // Ensure minimum readable size
    finalWidth = Math.max(finalWidth, 400);
    finalHeight = Math.max(finalHeight, 250);
    
      return {
        width: finalWidth,
        height: finalHeight
      };
    }, [viewportSize, isMobile, isLandscape]);
  
  const tableCenter = { x: tableDims.width / 2, y: tableDims.height / 2 };
  
  // Get global UI state
  const globalUIState = useMemo(() => {
    // CRITICAL: Pass allSeats to getGlobalUIState so it can find the local player index
    const uiState = getGlobalUIState({ 
      phase: state.phase, 
      gameState: state, 
      localPlayerID: player?.playerID,
      allSeats: allSeats // Pass the allSeats array from table.players + table.bots
    });
    console.log(`DEBUG: TableCentric - globalUIState computed. phase: ${state.phase}, showReadyButton: ${uiState.controls.showReadyButton}, readyPlayers:`, state.readyPlayers || [], `localPlayerID:`, player?.playerID);
    // Debug logging for controls
    if (state.phase === 'action') {
      console.log(`DEBUG: Global UI State - phase: ${state.phase}, controls:`, uiState.controls, `activePlayerIdx:`, state.activePlayerIdx, `localPlayerID:`, player?.playerID, `allSeats:`, allSeats.map((s, i) => `${i}:${s.playerID}`).join(', '));
    }
    return uiState;
  }, [state.phase, state.readyPlayers, state.suitVotes, state.readies, state.activePlayerIdx, state.knocked, state.discarded, state.winnerIdx, state.dealerIdx, state.dealerRevealFound, player?.playerID, allSeats]);
  
  // Calculate seat positions and UI states
  const seatData = useMemo(() => {
    return allSeats.map((p, idx) => {
      const position = calculateSeatPosition(
        idx,
        allSeats.length,
        tableDims.width,
        tableDims.height,
        80, // seatRadius - reduced distance between avatars and table
        myIdx >= 0 ? myIdx : 0 // Anchor local player at bottom
      );
      
      const seatState = getSeatUIState({
        seatIndex: idx,
        player: p,
        phase: state.phase,
        gameState: state,
        localPlayerID: player?.playerID
      });
      
      return { position, seatState, angle: position.angle };
    });
  }, [allSeats, tableDims, myIdx, state, player?.playerID]);
  
  if (!table || !player) {
    return (
      <div style={{ textAlign: 'center', marginTop: 100, fontSize: '1.5rem', color: '#64748b' }}>
        Loading game...
      </div>
    );
  }
  
  return (
    <div
      style={{
        display: 'flex',
        gap: isMobile ? (isLandscape ? '8px' : '5px') : '20px',
        padding: isMobile ? (isLandscape ? '8px' : '5px') : '20px',
        background: '#f1f5f9',
        minHeight: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        boxSizing: 'border-box',
        flexDirection: isMobile ? (isLandscape ? 'row' : 'column') : 'row', // Row for landscape, column for portrait
        position: 'relative'
      }}
    >
      {/* Main Table Area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: isMobile ? 'auto' : 'visible',
          minHeight: 0,
          padding: isMobile ? (isLandscape ? '10px' : '5px') : '60px 15px 100px 15px',
          width: isMobile && !isLandscape ? '100%' : 'auto',
          height: isMobile && isLandscape ? '100vh' : 'auto'
        }}
      >
        {/* Table Container - positioned to keep all seats visible */}
        <div
          style={{
            position: 'relative',
            width: `${tableDims.width}px`,
            height: `${tableDims.height}px`,
            margin: '0 auto' // Center the table
          }}
        >
          <TableFelt width={tableDims.width} height={tableDims.height}>
            {/* Pot Display (center) */}
            <PotDisplay 
              pot={state.pot || 0} 
              creditValue={state.creditValue || 1.00}
              phase={state.phase}
            />
            
            {/* Player Seats */}
            {seatData.map(({ position, seatState, angle }, idx) => {
              // Get this player's suit vote
              const playerSuitVote = state.suitVotes?.find(v => v.playerID === seatState.player.playerID)?.suit || null;
              // Check if player has a free hand
              const hasFreeHand = state.freeHands && state.freeHands[seatState.player.playerID] === true;
              
              return (
              <React.Fragment key={seatState.player.playerID}>
                {/* Seat (avatar + minimal info) */}
                <PlayerSeat
                  seatState={seatState}
                  position={position}
                  isLocalPlayer={seatState.isLocalPlayer}
                  dealerSuit={state.dealerSuit}
                  creditValue={state.creditValue || 1.00}
                  suitVote={playerSuitVote}
                  phase={state.phase}
                  hasFreeHand={hasFreeHand}
                />
                
                {/* Cards on felt - show during action, reveal, and results phases ONLY */}
                {/* DealAnimation handles deal phase exclusively - don't render PlayerHand during deal */}
                {(state.phase === 'action' || state.phase === 'reveal' || state.phase === 'results') && (() => {
                  // During action/reveal/results, PlayerHand is the primary renderer
                  // For action, reveal, and results phases
                  // CRITICAL: Get hand from state.hands directly using idx, fallback to seatState.hand
                  // The idx here is the seat index in the allSeats array, which should match state.hands array index
                  const handFromState = state.hands && Array.isArray(state.hands) ? state.hands[idx] : null;
                  const handToShow = handFromState || seatState.hand;
                  
                  // Debug logging for local player
                  if (seatState.isLocalPlayer) {
                    console.log(`DEBUG: Local player cards check - phase: ${state.phase}, idx: ${idx}, handToShow:`, handToShow, `handFromState:`, handFromState, `seatState.hand:`, seatState.hand, `state.hands:`, state.hands, `state.hands[${idx}]:`, state.hands?.[idx]);
                    console.log(`DEBUG: All hands in state:`, state.hands?.map((h, i) => `${i}:${h?.length || 0} cards`).join(', ') || 'NO HANDS ARRAY');
                    console.log(`DEBUG: state.hands type:`, typeof state.hands, `isArray:`, Array.isArray(state.hands), `length:`, state.hands?.length);
                  }
                  
                  // For local player in action phase, always show cards face up
                  // Also check if hand exists in state.hands even if seatState.hand is missing
                  if (handToShow && Array.isArray(handToShow) && handToShow.length === 2) {
                    const cardVisibility = seatState.cardVisibility || {
                      showCards: true,
                      showFaceUp: seatState.isLocalPlayer,
                      showFaceDown: !seatState.isLocalPlayer
                    };
                    // Override for local player - always show face up during action
                    if (seatState.isLocalPlayer && state.phase === 'action') {
                      cardVisibility.showCards = true;
                      cardVisibility.showFaceUp = true;
                      cardVisibility.showFaceDown = false;
                    }
                    console.log(`DEBUG: Rendering PlayerHand for ${seatState.isLocalPlayer ? 'local' : 'opponent'} player ${seatState.player.name} (idx ${idx}) - hand:`, handToShow, `cardVisibility:`, cardVisibility);
                    return (
                      <PlayerHand
                        key={`player-hand-${seatState.player.playerID}-${idx}`}
                        hand={handToShow}
                        seatPosition={position}
                        seatAngle={angle}
                        tableCenter={tableCenter}
                        cardVisibility={cardVisibility}
                        phase={state.phase}
                        isLocalPlayer={seatState.isLocalPlayer}
                        revealedIndex={state.revealedCount || -1}
                      />
                    );
                  } else if (seatState.isLocalPlayer && state.phase === 'action') {
                    console.warn(`WARNING: Local player has no hand! idx: ${idx}, handToShow:`, handToShow, `handFromState:`, handFromState, `seatState.hand:`, seatState.hand, `state.hands:`, state.hands, `state.hands length:`, state.hands?.length);
                  }
                  return null;
                })()}
                
                {/* Chips removed - chip count is shown in the notification block instead */}
              </React.Fragment>
              );
            })}
            
                        {/* Dealer reveal cards (if in dealer_reveal or dealer_selected phase) - show all revealed cards */}
                        {(state.phase === 'dealer_reveal' || state.phase === 'dealer_selected') && state.dealerRevealDeck && state.dealerRevealDeck.length > 0 && (
                          <DealerRevealCards
                            dealerRevealDeck={state.dealerRevealDeck}
                            dealerRevealIdx={state.dealerRevealIdx !== null && state.dealerRevealIdx !== undefined ? state.dealerRevealIdx : -1}
                            dealerSuit={state.dealerSuit}
                            seatData={seatData}
                            tableCenter={tableCenter}
                            totalSeats={allSeats.length}
                          />
                        )}
            
            {/* Deal animation - show cards being dealt during deal phase ONLY */}
            {/* Cards transition to PlayerHand component when phase changes to action */}
            {state.phase === 'deal' && state.hands && state.hands.length > 0 && (() => {
              console.log(`DEBUG: TableCentric - Rendering DealAnimation for deal phase. hands.length=${state.hands.length}, dealerIdx=${state.dealerIdx}, allSeats.length=${allSeats.length}`);
              return (
                <DealAnimation
                  key={`deal-animation-${state.phase}`}
                  hands={state.hands}
                  seatData={seatData}
                  tableCenter={tableCenter}
                  dealerIdx={state.dealerIdx}
                  allSeats={allSeats}
                  localPlayerID={player?.playerID}
                />
              );
            })()}
          </TableFelt>
        </div>
        
      </div>
      
      {/* HUD Panel (right sidebar) with Phase Controls below */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? (isLandscape ? '5px' : '8px') : '10px', 
        width: isMobile ? (isLandscape ? '200px' : '100%') : '280px',
        flexShrink: 0,
        position: isMobile && !isLandscape ? 'fixed' : 'relative',
        right: isMobile && !isLandscape ? 0 : 'auto',
        top: isMobile && !isLandscape ? 0 : 'auto',
        height: isMobile && !isLandscape ? '100vh' : 'auto',
        zIndex: isMobile && !isLandscape ? 1000 : 'auto',
        background: isMobile && !isLandscape ? 'rgba(0, 0, 0, 0.3)' : 'transparent'
      }}>
        <HudPanelContainer>
          <PlayersHudPanel
            allSeats={allSeats}
            gameState={state}
            localPlayerID={player.playerID}
            creditValue={state.creditValue || 1.00}
            hands={state.hands}
          />
        </HudPanelContainer>
        
        {/* Phase Controls below HUD - ensure it fits on screen */}
        <div style={{ 
          flexShrink: 0,
          width: '100%',
          maxWidth: isMobile ? (isLandscape ? '200px' : '100%') : '280px',
          maxHeight: isMobile ? (isLandscape ? `${Math.min(viewportSize.height * 0.3, 150)}px` : `${Math.min(viewportSize.height * 0.25, 120)}px`) : `${Math.min(viewportSize.height * 0.4, 200)}px`,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          <PhaseControls
            controls={globalUIState.controls}
            phase={state.phase}
            gameState={state}
            socket={socket}
            tableID={table.id}
            playerID={player.playerID}
          />
        </div>
      </div>
      
      {/* Dealer No Face Card Notice */}
      {state.showDealerNoFaceCardNotice && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          maxWidth: 500,
          background: "#f59e0b", // Warning orange
          color: "white",
          fontWeight: "700",
          fontSize: "1.25rem",
          border: "2px solid #f59e0b",
          borderRadius: "16px",
          textAlign: "center",
          padding: "32px 40px",
          zIndex: 1000,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px"
        }}>
          <div style={{
            fontSize: "2rem",
            marginBottom: "8px"
          }}>
            ⚠️
          </div>
          <div>
            Dealer cannot knock without face cards!
          </div>
          <div style={{
            fontSize: "0.9rem",
            opacity: 0.9
          }}>
            The dealer must discard if they have no face cards (J, Q, K).
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DealerRevealCards Component
 * Shows cards being revealed during dealer selection
 * Shows all cards revealed so far (from 0 to dealerRevealIdx)
 */
function DealerRevealCards({ dealerRevealDeck, dealerRevealIdx, dealerSuit, seatData, tableCenter, totalSeats }) {
  const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
  const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
  
  if (!dealerRevealDeck || dealerRevealDeck.length === 0) return null;
  
  // Show all cards that have been revealed so far
  // If dealerRevealIdx is -1 or null, show the first card (index 0)
  const maxIdx = dealerRevealIdx >= 0 ? dealerRevealIdx : 0;
  const revealedCards = [];
  
  for (let i = 0; i <= maxIdx && i < dealerRevealDeck.length; i++) {
    const card = dealerRevealDeck[i];
    if (!card) continue;
    
    const seatIndex = i % totalSeats;
    const seat = seatData[seatIndex];
    if (seat) {
      revealedCards.push({ 
        card, 
        seatIndex, 
        seat, 
        isLatest: i === maxIdx,
        cardIndex: i
      });
    }
  }
  
  // If no cards revealed yet but we're in dealer_reveal phase, show first card
  if (revealedCards.length === 0 && dealerRevealDeck.length > 0) {
    const card = dealerRevealDeck[0];
    const seatIndex = 0 % totalSeats;
    const seat = seatData[seatIndex];
    if (seat && card) {
      revealedCards.push({ 
        card, 
        seatIndex, 
        seat, 
        isLatest: true,
        cardIndex: 0
      });
    }
  }
  
  if (revealedCards.length === 0) {
    // Debug: log why no cards are showing
    console.log('DealerRevealCards: No cards to show', {
      dealerRevealDeck: dealerRevealDeck?.length,
      dealerRevealIdx,
      totalSeats,
      seatDataLength: seatData?.length
    });
    return null;
  }
  
  return (
    <>
      {revealedCards.map(({ card, seatIndex, seat, isLatest, cardIndex = idx }, idx) => {
        if (!card || !seat || !seat.position) return null;
        
        // Position card at the card position on the table (not on avatar)
        // For dealer reveal, always use first card position (0) since we're revealing one card at a time
        const cardPos = calculateCardPosition(
          seat.position.x,
          seat.position.y,
          seat.angle,
          0, // Always use first card position for dealer reveal
          tableCenter.x,
          tableCenter.y
        );
        
        const isMatch = card && card.suit === dealerSuit;
        
        return (
          <div
            key={`dealer-reveal-${cardIndex ?? idx}-${idx}`}
            style={{
              position: 'absolute',
              left: `${cardPos.x}px`,
              top: `${cardPos.y}px`,
              transform: `translate(-50%, -50%) rotate(${cardPos.rotation}deg)`,
              zIndex: isLatest ? 9 : 8,
              animation: isLatest ? 'cardReveal 0.5s ease' : 'none',
              opacity: isLatest ? 1 : 0.8,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                width: '48px', // Reduced by 20% (60px * 0.8)
                height: '67px', // Reduced by 20% (84px * 0.8)
                background: isMatch ? '#fef3c7' : 'white',
                border: isMatch ? '3px solid #fbbf24' : '2px solid #333',
                borderRadius: '8px',
                boxShadow: isMatch 
                  ? '0 0 20px rgba(251, 191, 36, 0.8), 0 4px 12px rgba(0, 0, 0, 0.3)'
                  : '0 4px 12px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                padding: '5px', // Reduced by 20%
                boxSizing: 'border-box'
              }}
            >
              <div style={{ fontSize: '0.96rem', fontWeight: '700', color: suitColors[card?.suit] || '#333' }}>
                {card?.rank || ''} {suitSymbols[card?.suit] || ''}
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', color: suitColors[card?.suit] || '#333' }}>
                {suitSymbols[card?.suit] || ''}
              </div>
            </div>
            {isMatch && (
              <div style={{ 
                fontSize: '0.7rem', 
                color: '#f59e0b', 
                fontWeight: '700', 
                textAlign: 'center',
                marginTop: '5px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                whiteSpace: 'nowrap'
              }}>
                DEALER!
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * DealAnimation Component
 * Shows cards being dealt with animation during deal phase
 * Deals cards clockwise around the table, one card per player at a time
 * First round: first card to each player, Second round: second card to each player
 */
function DealAnimation({ hands, seatData, tableCenter, dealerIdx, allSeats, localPlayerID }) {
  const [dealtCardKeys, setDealtCardKeys] = useState(new Set());
  
  useEffect(() => {
    // Reset when hands change
    setDealtCardKeys(new Set());
    
    // Animate dealing cards one by one
    if (!hands || hands.length === 0 || dealerIdx === null || dealerIdx === undefined) {
      console.log(`DEBUG: DealAnimation - missing data: hands=${hands?.length || 0}, dealerIdx=${dealerIdx}, allSeats.length=${allSeats.length}`);
      return;
    }
    
    const numPlayers = allSeats.length;
    const cardsPerPlayer = 2;
    const totalCards = numPlayers * cardsPerPlayer;
    
    if (totalCards === 0) {
      console.log(`DEBUG: DealAnimation - totalCards is 0`);
      return;
    }
    
    console.log(`DEBUG: DealAnimation - Starting deal animation: ${numPlayers} players, ${totalCards} total cards, dealerIdx=${dealerIdx}`);
    
    // Create array of card indices in deal order: clockwise from dealer's left
    // First round: first card to each player (starting from dealer+1)
    // Second round: second card to each player (starting from dealer+1)
    const dealOrder = [];
    const dealOrderDetails = []; // For debugging
    for (let round = 0; round < cardsPerPlayer; round++) {
      for (let offset = 0; offset < numPlayers; offset++) {
        const seatIdx = (dealerIdx + 1 + offset) % numPlayers;
        const cardInHandIdx = round;
        // Calculate global card index: (seatIdx * cardsPerPlayer) + cardInHandIdx
        const globalCardIdx = (seatIdx * cardsPerPlayer) + cardInHandIdx;
        dealOrder.push(globalCardIdx);
        dealOrderDetails.push({
          round: round + 1,
          offset,
          seatIdx,
          cardInHandIdx,
          globalCardIdx,
          playerName: allSeats[seatIdx]?.name || `Player ${seatIdx}`
        });
      }
    }
    
    console.log(`DEBUG: DealAnimation - Deal order (${dealOrder.length} cards):`, dealOrder);
    console.log(`DEBUG: DealAnimation - Deal order details:`, dealOrderDetails);
    
    // Deal first card after a short delay to ensure component is mounted
    let cardOrderIndex = 0;
    const dealInterval = setInterval(() => {
      if (cardOrderIndex < dealOrder.length) {
        setDealtCardKeys(prev => {
          const newSet = new Set(prev);
          newSet.add(dealOrder[cardOrderIndex]);
          console.log(`DEBUG: DealAnimation - Dealt card ${cardOrderIndex + 1}/${dealOrder.length} (index ${dealOrder[cardOrderIndex]}, seatIdx: ${dealOrderDetails[cardOrderIndex]?.seatIdx}, cardInHandIdx: ${dealOrderDetails[cardOrderIndex]?.cardInHandIdx}, player: ${dealOrderDetails[cardOrderIndex]?.playerName})`);
          return newSet;
        });
        cardOrderIndex++;
      } else {
        console.log(`DEBUG: DealAnimation - All cards dealt, clearing interval`);
        clearInterval(dealInterval);
      }
    }, 2000); // 2000ms (2 seconds) between each card - same as dealer selection phase
    
    return () => {
      console.log(`DEBUG: DealAnimation - Cleaning up interval`);
      clearInterval(dealInterval);
    };
  }, [hands, dealerIdx, allSeats.length]);
  
  if (!hands || hands.length === 0) return null;
  
  const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
  const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };
  
  return (
    <>
      {hands.map((hand, seatIdx) => {
        if (!hand || hand.length === 0) return null;
        const seat = seatData[seatIdx];
        if (!seat) return null;
        
        const isLocalPlayer = allSeats[seatIdx]?.playerID === localPlayerID;
        
        return hand.map((card, cardInHandIdx) => {
          // Calculate global card index: (seatIdx * cardsPerPlayer) + cardInHandIdx
          const globalCardIdx = (seatIdx * 2) + cardInHandIdx;
          const isDealt = dealtCardKeys.has(globalCardIdx);
          
          // Debug logging for local player
          if (isLocalPlayer) {
            console.log(`DEBUG: DealAnimation - Card check: seatIdx=${seatIdx}, cardInHandIdx=${cardInHandIdx}, globalCardIdx=${globalCardIdx}, isDealt=${isDealt}, dealtCardKeys size=${dealtCardKeys.size}, keys:`, Array.from(dealtCardKeys).sort((a,b) => a-b));
          }
          
          if (!isDealt) {
            // Card not yet dealt - don't render
            return null;
          }
          
          const cardPos = calculateCardPosition(
            seat.position.x,
            seat.position.y,
            seat.angle,
            cardInHandIdx,
            tableCenter.x,
            tableCenter.y
          );
          
          // Debug logging for local player
          if (isLocalPlayer) {
            console.log(`DEBUG: DealAnimation - Rendering card ${cardInHandIdx} for local player (seatIdx ${seatIdx}, globalCardIdx ${globalCardIdx}) at position:`, cardPos);
          }
          
          // Cards appear immediately when added to dealtCardKeys (no additional delay needed)
          // The delay is handled by the setInterval in the useEffect
          return (
            <DealCard
              key={`deal-${seatIdx}-${cardInHandIdx}-${globalCardIdx}`}
              card={card}
              position={cardPos}
              faceUp={isLocalPlayer}
              isLocalPlayer={isLocalPlayer}
              suitSymbols={suitSymbols}
              suitColors={suitColors}
              dealDelay={0} // No delay - card appears immediately when isDealt becomes true
              cardIndex={cardInHandIdx} // Pass cardIndex for z-index layering
            />
          );
        });
      })}
    </>
  );
}

/**
 * DealCard Component
 * Single card rendered during deal animation
 */
function DealCard({ card, position, faceUp, isLocalPlayer, suitSymbols, suitColors, dealDelay = 0, cardIndex = 0 }) {
  // Card appears immediately when rendered - delay is handled by setInterval in DealAnimation
  // This component is only rendered when the card should be visible (isDealt === true)
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
        width: '50px', // Increased by 5% (48px * 1.05 = 50.4px, rounded to 50px)
        height: '70px', // Increased by 5% (67px * 1.05 = 70.35px, rounded to 70px)
        zIndex: isLocalPlayer ? (20 + cardIndex) : (10 + cardIndex), // Right card (cardIndex 1) on top of left card (cardIndex 0)
        animation: 'cardDeal 0.4s ease',
        opacity: 1 // Always visible when rendered
      }}
    >
      {faceUp ? (
        // Face up card
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'white',
            border: '2px solid #333',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            padding: '5px', // Reduced by 20%
            boxSizing: 'border-box'
          }}
        >
          <div style={{ fontSize: '0.96rem', fontWeight: '700', color: suitColors[card.suit] || '#333' }}>
            {card.rank} {suitSymbols[card.suit] || card.suit}
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: suitColors[card.suit] || '#333' }}>
            {suitSymbols[card.suit] || card.suit}
          </div>
          <div style={{ fontSize: '0.96rem', fontWeight: '700', color: suitColors[card.suit] || '#333', transform: 'rotate(180deg)' }}>
            {card.rank} {suitSymbols[card.suit] || card.suit}
          </div>
        </div>
      ) : (
        // Face down card
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            border: '2px solid #1e3a8a',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{ fontSize: '1.6rem', color: 'white', opacity: 0.8 }}>
            🎴
          </div>
        </div>
      )}
    </div>
  );
}

// Add card animations
const style = document.createElement('style');
style.textContent = `
  @keyframes cardReveal {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.5) rotateY(90deg);
    }
    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1) rotateY(0deg);
    }
  }
  @keyframes cardDeal {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.3);
    }
    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;
if (!document.head.querySelector('#card-animations')) {
  style.id = 'card-animations';
  document.head.appendChild(style);
}

